import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, X } from 'lucide-react';

/**
 * The shell every Clients list uses — follow-up lanes and the customer/vehicle
 * browse screens alike.
 *
 * One component so the six screens cannot drift into six slightly different
 * lists. It owns the chrome (back, title, search, empty and loading states) and
 * nothing else; each screen supplies its own rows.
 *
 * These are ROUTES, not modals, and that is deliberate: the app ships as an
 * installed PWA, and Android's back button does not close a modal unless the
 * component wires up history itself. A route gets back, deep links and a
 * shareable URL for free.
 */
interface ListScreenProps<T> {
    title: string;
    subtitle?: string;
    icon: React.ElementType;
    items: T[];
    loading: boolean;
    /** Free-text haystack for the search box. Lowercased for you. */
    searchText: (item: T) => string;
    searchPlaceholder?: string;
    renderItem: (item: T) => React.ReactNode;
    keyOf: (item: T) => string;
    /** Shown when there is nothing at all (as opposed to nothing matching). */
    emptyTitle: string;
    emptyBody: string;
    /** Where the back arrow goes. */
    backTo?: string;
    /** Rendered under the search box — a filter row, a note, anything. */
    children?: React.ReactNode;
}

export function ListScreen<T>({
    title,
    subtitle,
    icon: Icon,
    items,
    loading,
    searchText,
    searchPlaceholder = 'Search…',
    renderItem,
    keyOf,
    emptyTitle,
    emptyBody,
    backTo = '/clients',
    children,
}: ListScreenProps<T>) {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    // How many rows are actually mounted. A workshop with 800 customers would
    // otherwise pay for 800 rows on a mid-range Android just to read the first
    // ten. Grows on demand rather than paginating, because a worklist is read
    // top-down and "load more" is one tap in the direction you were already
    // scrolling.
    const PAGE = 40;
    const [visible, setVisible] = useState(PAGE);

    // Reset paging when the list or the query changes, by comparing during
    // render rather than from an effect — an effect would paint the old page
    // size for a frame first, and React flags the cascade.
    const resetKey = `${query}|${items.length}`;
    const [seenKey, setSeenKey] = useState(resetKey);
    if (seenKey !== resetKey) {
        setSeenKey(resetKey);
        setVisible(PAGE);
    }

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter((it) => searchText(it).toLowerCase().includes(q));
    }, [items, query, searchText]);

    const shown = filtered.slice(0, visible);

    return (
        <div className="w-full space-y-5">

            {/* Header */}
            <div className="flex items-start gap-3 min-w-0">
                <button
                    onClick={() => navigate(backTo)}
                    className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full bg-tint/5 hover:bg-tint/10 transition-colors shrink-0"
                    aria-label="Back"
                >
                    <ArrowLeft className="w-5 h-5 text-fg-3" />
                </button>
                <div className="min-w-0 flex-1">
                    <h1 className="text-2xl font-bold text-fg tracking-tight flex items-center gap-2 min-w-0">
                        <Icon className="w-5 h-5 text-accent shrink-0" />
                        <span className="truncate">{title}</span>
                    </h1>
                    {subtitle && <p className="text-sm text-fg-4 mt-0.5">{subtitle}</p>}
                </div>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-4 pointer-events-none" />
                <input
                    ref={inputRef}
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    className="w-full h-12 pl-9 pr-10 bg-panel border border-line rounded-xl text-fg outline-none focus:border-accent transition-colors placeholder:text-fg-4"
                />
                {query && (
                    <button
                        onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-fg-4 hover:text-fg transition-colors"
                        aria-label="Clear search"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {children}

            {/* Rows */}
            {loading ? (
                <div className="space-y-2" aria-busy="true">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-20 rounded-xl bg-panel animate-pulse" />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className="rounded-xl border border-line bg-panel p-8 text-center">
                    <p className="font-bold text-fg">{emptyTitle}</p>
                    <p className="text-sm text-fg-4 mt-1.5 max-w-sm mx-auto">{emptyBody}</p>
                </div>
            ) : filtered.length === 0 ? (
                <div className="rounded-xl border border-line bg-panel p-8 text-center">
                    <p className="font-bold text-fg">Nothing matches “{query}”</p>
                    <p className="text-sm text-fg-4 mt-1.5">Try a name, a registration, or part of one.</p>
                </div>
            ) : (
                <>
                    <p className="text-fd-3xs uppercase tracking-wider text-fg-4 font-bold">
                        {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
                    </p>
                    <div className="space-y-2">
                        {shown.map((item) => (
                            <div key={keyOf(item)}>{renderItem(item)}</div>
                        ))}
                    </div>
                    {filtered.length > visible && (
                        <button
                            onClick={() => setVisible((v) => v + PAGE)}
                            className="w-full min-h-[44px] rounded-xl border border-line bg-panel text-sm font-bold text-accent hover:bg-tint/5 transition-colors"
                        >
                            Show more ({filtered.length - visible} remaining)
                        </button>
                    )}
                </>
            )}
        </div>
    );
}
